import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { lessonPaymentsApi } from '@/api/lessonPayments'
import type { LessonParticipant } from '@/types'
import type {
  GroupLessonPaymentEntry,
  GroupLessonPaymentState,
  IndividualLessonPaymentState,
  LessonPaymentMethod,
} from '@/types/billing'
import { isGroupPaymentState } from '@/types/billing'
import { getErrorMessage } from '@/utils/error'
import { formatMoney } from './formatMoney'
import { PaymentStatusChip } from './PaymentStatusChip'

const FINALIZE_METHODS: LessonPaymentMethod[] = ['from_balance', 'cash', 'deferred']

export type LessonPaymentStepHandle = {
  submit: () => Promise<boolean>
}

type Props = {
  lessonId: number
  participants: LessonParticipant[]
  enabled: boolean
  onCanSubmitChange?: (canSubmit: boolean) => void
  onSubmittingChange?: (submitting: boolean) => void
}

export const LessonPaymentStep = forwardRef<LessonPaymentStepHandle, Props>(function LessonPaymentStep(
  { lessonId, participants, enabled, onCanSubmitChange, onSubmittingChange },
  ref,
) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [paymentState, setPaymentState] = useState<
    IndividualLessonPaymentState | GroupLessonPaymentState | null
  >(null)
  const [individualMethod, setIndividualMethod] = useState<LessonPaymentMethod>('from_balance')
  const [groupMethods, setGroupMethods] = useState<Record<number, LessonPaymentMethod>>({})

  useEffect(() => {
    if (!enabled || !Number.isFinite(lessonId)) return
    setLoading(true)
    setError(null)
    lessonPaymentsApi
      .get(lessonId)
      .then((state) => {
        setPaymentState(state)
        if (isGroupPaymentState(state)) {
          const map: Record<number, LessonPaymentMethod> = {}
          for (const entry of state.entries) {
            if (entry.payment_status === 'pending') {
              map[entry.student_id] = entry.payment_method ?? 'from_balance'
            }
          }
          setGroupMethods(map)
        } else if (state.payment_method && FINALIZE_METHODS.includes(state.payment_method)) {
          setIndividualMethod(state.payment_method)
        }
      })
      .catch((e: unknown) => {
        setError(getErrorMessage(e) || t('common.error'))
      })
      .finally(() => setLoading(false))
  }, [enabled, lessonId, t])

  const pendingGroupEntries = useMemo(() => {
    if (!paymentState || !isGroupPaymentState(paymentState)) return [] as GroupLessonPaymentEntry[]
    return paymentState.entries.filter((e) => e.payment_status === 'pending')
  }, [paymentState])

  const canSubmit = Boolean(
    paymentState &&
      (isGroupPaymentState(paymentState)
        ? pendingGroupEntries.length > 0
        : paymentState.payment_status === 'pending'),
  )

  useEffect(() => {
    onCanSubmitChange?.(canSubmit)
  }, [canSubmit, onCanSubmitChange])

  useEffect(() => {
    onSubmittingChange?.(submitting)
  }, [submitting, onSubmittingChange])

  const participantName = (userId: number) => {
    const p = participants.find((x) => x.user_id === userId)
    const u = p?.user
    return (
      u?.display_name ||
      [u?.surname, u?.first_name].filter(Boolean).join(' ') ||
      u?.email ||
      `#${userId}`
    )
  }

  const submit = useCallback(async (): Promise<boolean> => {
    if (!Number.isFinite(lessonId) || !paymentState || !canSubmit) return false
    setSubmitting(true)
    setError(null)
    try {
      if (isGroupPaymentState(paymentState)) {
        await lessonPaymentsApi.finalize(lessonId, {
          entries: pendingGroupEntries.map((entry) => ({
            student_id: entry.student_id,
            payment_method: groupMethods[entry.student_id] ?? 'from_balance',
          })),
        })
      } else {
        await lessonPaymentsApi.finalize(lessonId, { payment_method: individualMethod })
      }
      return true
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('common.error'))
      return false
    } finally {
      setSubmitting(false)
    }
  }, [canSubmit, groupMethods, individualMethod, lessonId, paymentState, pendingGroupEntries, t])

  useImperativeHandle(ref, () => ({ submit }), [submit])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Skeleton variant="rounded" height={40} />
        <Skeleton variant="rounded" height={40} />
      </Box>
    )
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {paymentState && !isGroupPaymentState(paymentState) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">{t('billing.paymentStatusLabel', 'Статус оплаты')}:</Typography>
            <PaymentStatusChip status={paymentState.payment_status} />
          </Box>
          {paymentState.price_amount != null && (
            <Typography variant="body2">
              {t('billing.lessonPrice', 'Цена')}:{' '}
              {formatMoney(paymentState.price_amount, paymentState.price_currency ?? 'RUB')}
            </Typography>
          )}
          {paymentState.payment_status === 'paid' ? (
            <Typography variant="body2" color="text.secondary">
              {t('billing.alreadyPaid', 'Оплата уже закрыта')}
            </Typography>
          ) : paymentState.payment_status === 'not_required' ? (
            <Typography variant="body2" color="text.secondary">
              {t('billing.paymentNotRequired', 'Оплата не требуется')}
            </Typography>
          ) : (
            <FormControl size="small" fullWidth>
              <InputLabel>{t('billing.paymentMethod', 'Способ оплаты')}</InputLabel>
              <Select
                label={t('billing.paymentMethod', 'Способ оплаты')}
                value={individualMethod}
                onChange={(e) => setIndividualMethod(e.target.value as LessonPaymentMethod)}
              >
                {FINALIZE_METHODS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {t(`billing.paymentMethodOption.${m}`, m)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      )}
      {paymentState && isGroupPaymentState(paymentState) && (
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('billing.groupPaymentHint', 'Укажите способ оплаты для каждого участника')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('billing.student', 'Ученик')}</TableCell>
                <TableCell>{t('billing.paymentStatusLabel', 'Статус')}</TableCell>
                <TableCell>{t('billing.paymentMethod', 'Способ оплаты')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentState.entries.map((entry) => (
                <TableRow key={entry.student_id}>
                  <TableCell>{participantName(entry.student_id)}</TableCell>
                  <TableCell>
                    <PaymentStatusChip status={entry.payment_status} />
                  </TableCell>
                  <TableCell>
                    {entry.payment_status === 'pending' ? (
                      <Select
                        size="small"
                        value={groupMethods[entry.student_id] ?? 'from_balance'}
                        onChange={(e) =>
                          setGroupMethods((prev) => ({
                            ...prev,
                            [entry.student_id]: e.target.value as LessonPaymentMethod,
                          }))
                        }
                      >
                        {FINALIZE_METHODS.map((m) => (
                          <MenuItem key={m} value={m}>
                            {t(`billing.paymentMethodOption.${m}`, m)}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : entry.payment_method ? (
                      t(`billing.paymentMethodOption.${entry.payment_method}`, entry.payment_method)
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  )
})
